import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AiFillCheckCircle, AiOutlineInbox, AiOutlineSearch } from 'react-icons/ai';

import { Container } from './styles';

import CadastroGenerico, { ICampos, IFormatoCampos } from '../../components/CadastroGenerico';

import Context, { IContext } from '../../context/Context';
import api from '../../services/api';
import { cpfMask } from '../../utils/validaCpfCnpj';
import { IReserva } from '../../types/Reserva';
import { IConvidado } from '../../types/Convidado';

// Busca sem diferenciar maiúsculas/minúsculas e acentos
function normalizarBusca(texto: string) {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export default function ReservaDetalhes() {
  const { cod } = useParams();

  const { setIsLoadingOverlay, usuario, empresa }: IContext = useContext(Context);

  const [reserva, setReserva] = useState<IReserva>();
  const [convidados, setConvidados] = useState<IConvidado[]>([]);
  const [busca, setBusca] = useState('');

  async function getData() {
    try {
      if (!cod) return;

      setIsLoadingOverlay(true);

      const [reservasResponse, convidadosResponse] = await Promise.all([
        api.get(`/reservas/${cod}`),
        api.get('/convidados', {
          params: {
            codres: cod
          }
        })
      ]);

      // Processar as respostas se todas forem bem-sucedidas
      if (reservasResponse.status === 200) {
        setReserva(reservasResponse.data);
      }

      if (convidadosResponse.status === 200) {
        setConvidados(convidadosResponse.data);
      }

    } catch (error: any) {
      if (error.response?.data?.erro) {
        toast.error(error.response.data.erro);
      } else {
        toast.error('Erro ao buscar dados');
      }
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function verificarConvidado(cod: number, che: boolean) {
    try {
      setIsLoadingOverlay(true);

      const response = await api.patch(`/convidados/${cod}`, {
        che: che
      });

      if (response.status === 200) {
        setConvidados(convidados.map(convidado => convidado.cod === cod ? { ...convidado, che } : convidado));
      }
    } catch (error) {
      toast.error('Erro ao verificar convidado');
    } finally {
      setIsLoadingOverlay(false);
    }
  }

  function getCadastroProps(entidade: string, endpoint: string, campos: ICampos[], formatoCampos?: IFormatoCampos) {
    return {
      entidade,
      endpoint,
      campos,
      formatoCampos
    }
  }

  const campos = [
    {
      campo: 'con',
      label: 'Nome Completo',
      type: 'text'
    },
    {
      campo: 'obs',
      label: 'Observação',
      type: 'text'
    }
  ] as ICampos[];

  // CPF do convidado fica oculto para o cliente 1
  const exibirCgc = !!empresa && empresa?.cod !== 1;

  if (exibirCgc) {
    campos.push({ campo: 'cgc', label: 'CPF/CNPJ', type: 'text' });
  }

  useEffect(() => {
    getData();
  }, []);

  if (reserva?.usuario.cod === Number(usuario?.cod) || (usuario?.tip === 'ADMINISTRADOR' || usuario?.tip === 'ASSISTENTE')) {
    return (
      <Container>
        {(reserva && convidados) &&
          <CadastroGenerico
            {...getCadastroProps(
              'Convidado',
              '/convidados',
              campos,
              {
                cgc: cpfMask
              }
            )}
            reserva={reserva}
            convidados={convidados}
          />}
      </Container>
    );
  }

  const termoBusca = normalizarBusca(busca.trim());
  const convidadosFiltrados = convidados.filter(convidado =>
    !termoBusca || normalizarBusca(`${convidado.con} ${convidado.obs || ''} ${exibirCgc ? convidado.cgc || '' : ''}`).includes(termoBusca)
  );
  const totalVerificados = convidados.filter(convidado => convidado.che).length;

  return (
    <Container>
      <div className='w-full max-w-5xl mx-auto px-4 pb-10'>
        <header className='mb-6 mt-4'>
          <h1 className='text-2xl font-semibold text-zinc-900 md:text-3xl'>Lista de Convidados</h1>
          <p className='mt-1 text-zinc-500' aria-live='polite'>
            {totalVerificados} de {convidados.length} {convidados.length === 1 ? 'convidado verificado' : 'convidados verificados'}
          </p>
        </header>

        {reserva && (
          <section className='mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-card'>
            <dl className='grid grid-cols-2 gap-4 text-sm md:grid-cols-4'>
              <div>
                <dt className='text-zinc-500'>Data</dt>
                <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.datres}</dd>
              </div>
              <div>
                <dt className='text-zinc-500'>Espaço</dt>
                <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.espaco.esp}</dd>
              </div>
              {reserva.tipres?.des && (
                <div>
                  <dt className='text-zinc-500'>Tipo de reserva</dt>
                  <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.tipres.des}</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        <div className='relative mb-4'>
          <AiOutlineSearch className='pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400' aria-hidden='true' />
          <input
            type='search'
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder='Buscar convidado…'
            aria-label='Buscar convidado'
            className='h-12 w-full rounded-xl border border-zinc-300 bg-white pl-11 pr-4 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10'
          />
        </div>

        <section className='overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-card'>
          {convidadosFiltrados.length === 0 ? (
            <div className='flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-zinc-400'>
              <AiOutlineInbox className='h-8 w-8' aria-hidden='true' />
              {termoBusca ? `Nenhum convidado encontrado para “${busca.trim()}”.` : 'Nenhum convidado cadastrado.'}
            </div>
          ) : (
            <ul className='divide-y divide-zinc-100'>
              {convidadosFiltrados.map(convidado => (
                <li key={convidado.cod} className='flex items-center gap-3 px-4 py-4 sm:px-5'>
                  <div className='min-w-0 flex-1'>
                    <p className={`break-words font-semibold ${convidado.che ? 'text-zinc-500' : 'text-zinc-900'}`}>{convidado.con}</p>
                    {(exibirCgc && convidado.cgc) && <p className='mt-0.5 text-sm text-zinc-500'>{convidado.cgc}</p>}
                    {convidado.obs && (
                      <p className='mt-0.5 break-words text-sm text-zinc-500'><span className='font-medium text-zinc-700'>Obs:</span> {convidado.obs}</p>
                    )}
                  </div>
                  <button
                    type='button'
                    role='switch'
                    aria-checked={!!convidado.che}
                    aria-label={`Verificar ${convidado.con}`}
                    onClick={() => verificarConvidado(convidado.cod, !convidado.che)}
                    className={`flex h-10 flex-none items-center gap-1.5 rounded-xl px-4 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-1 ${convidado.che ?
                      'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' :
                      'border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50'}`}
                  >
                    {convidado.che && <AiFillCheckCircle aria-hidden='true' />}
                    {convidado.che ? 'Confirmado' : 'Confirmar Check-in'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Container>
  );

}
